import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface Snackbar {
  id: string
  show: boolean
  message: string
  color: string
  timeout: number
}

export const useNotificationStore = defineStore('notifications', () => {
  const snackbars = ref<Snackbar[]>([])
  let nextId = 0

  const addSnackbar = (message: string, color: string, timeout: number) => {
    const id = `snackbar-${nextId++}`
    snackbars.value.push({
      id,
      show: true,
      message,
      color,
      timeout
    })

    // Auto-remove after timeout
    setTimeout(() => {
      removeSnackbar(id)
    }, timeout)
  }

  const removeSnackbar = (id: string) => {
    const index = snackbars.value.findIndex(s => s.id === id)
    if (index !== -1) {
      snackbars.value.splice(index, 1)
    }
  }

  const showSuccess = (message: string, timeout = 4000) => {
    addSnackbar(message, 'success', timeout)
  }

  const showError = (message: string, timeout = 6000) => {
    addSnackbar(message, 'error', timeout)
  }

  const showWarning = (message: string, timeout = 5000) => {
    addSnackbar(message, 'warning', timeout)
  }

  const showInfo = (message: string, timeout = 4000) => {
    addSnackbar(message, 'info', timeout)
  }

  const hide = (id: string) => {
    removeSnackbar(id)
  }

  // Legacy support - keep single snackbar for backward compatibility
  const snackbar = ref<Snackbar>({
    id: '',
    show: false,
    message: '',
    color: 'info',
    timeout: 4000
  })

  return {
    snackbar, // For backward compatibility
    snackbars,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hide,
    removeSnackbar
  }
})
